// The in-app school pages every HikmahVerses app shows to a school's staff, modelled on Quranic Poems:
//
//   Principal - "My School":  Overview / Teachers (add a teacher, see each teacher) / Students / Progress / Notices
//   Teacher   - "My Students": their classes, each student in detail / Notices
//
// The school itself (teachers, students, classes, licences, progress, notice board) lives on the platform
// gateway - one record shared by every app - so this page only talks to the gateway's /school/* endpoints with the
// app's own sign-in token. A principal sees the whole school; a teacher only the classes assigned to them (the
// gateway enforces both, this file only hides what would be refused).
//
// This file is intentionally identical in every app (no imports from the host app, its own scoped styles), so a
// fix made here is copied to all of them. The host passes an `api` made by createSchoolApi().
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

// ------------------------------------------------------------------ api
export function createSchoolApi({ gatewayUrl, getToken }) {
  const base = String(gatewayUrl || "").replace(/\/+$/, "");
  async function call(method, path, body) {
    const token = getToken();
    const res = await fetch(`${base}${path}`, {
      method,
      credentials: "include",
      headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(body ? { "Content-Type": "application/json" } : {}) },
      body: body ? JSON.stringify(body) : undefined,
    });
    let data = null;
    try { data = await res.json(); } catch { /* empty body */ }
    if (!res.ok) {
      const d = data && data.detail;
      const err = new Error(typeof d === "string" ? d : Array.isArray(d) ? (d[0]?.msg || "Invalid request") : "Request failed");
      err.status = res.status;
      throw err;
    }
    return data;
  }
  // The progress report (PDF or Excel): a file, so it is read as a blob rather than JSON.
  async function report(period, format, product) {
    const token = getToken();
    const res = await fetch(`${base}/school/report?period=${encodeURIComponent(period)}&format=${encodeURIComponent(format)}${product ? `&product=${encodeURIComponent(product)}` : ""}`, {
      credentials: "include", headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
    if (!res.ok) {
      let d = null; try { d = (await res.json()).detail; } catch { /* not JSON */ }
      throw new Error(typeof d === "string" ? d : "Could not build the report");
    }
    const cd = res.headers.get("Content-Disposition") || "";
    return { blob: await res.blob(), name: (cd.match(/filename="?([^";]+)"?/) || [])[1] || `school-progress_${period}.${format}` };
  }
  const id = encodeURIComponent;
  return {
    report,
    appProgress: (product) => call("GET", `/school/progress?product=${encodeURIComponent(product)}`),
    me: () => call("GET", "/school/me"),
    students: () => call("GET", "/school/students"),
    student: (sid) => call("GET", `/school/students/${id(sid)}`),
    addStudents: (students) => call("POST", "/school/students", { students }),
    resetStudent: (sid) => call("POST", `/school/students/${id(sid)}/reset-password`),
    removeStudent: (sid) => call("DELETE", `/school/students/${id(sid)}`),
    teachers: () => call("GET", "/school/teachers"),
    addTeacher: (t) => call("POST", "/school/teachers", t),
    setTeacherClasses: (tid, classes) => call("PUT", `/school/teachers/${id(tid)}/classes`, { classes }),
    resetTeacher: (tid) => call("POST", `/school/teachers/${id(tid)}/reset-password`),
    removeTeacher: (tid) => call("DELETE", `/school/teachers/${id(tid)}`),
    analytics: () => call("GET", "/school/analytics"),
    notices: () => call("GET", "/school/notices"),
    staffCount: () => call("GET", "/school/notices/staff-count"),
    createNotice: (n) => call("POST", "/school/notices", n),
    updateNotice: (nid, patch) => call("PATCH", `/school/notices/${nid}`, patch),
    deleteNotice: (nid) => call("DELETE", `/school/notices/${nid}`),
    markRead: (nid) => call("POST", `/school/notices/${nid}/read`),
  };
}

// True once, for school staff only: the host app uses it to decide whether to show the school link. A 403 just
// means "not school staff"; any other failure also hides the link rather than showing a broken page.
export function useIsSchoolStaff(api, signedIn) {
  const [state, setState] = useState({ checked: false, staff: false, role: null });
  useEffect(() => {
    let alive = true;
    if (!signedIn || !api) { setState({ checked: true, staff: false, role: null }); return undefined; }
    api.me().then((m) => alive && setState({ checked: true, staff: true, role: m.role }))
      .catch(() => alive && setState({ checked: true, staff: false, role: null }));
    return () => { alive = false; };
  }, [api, signedIn]);
  return state;
}

/** The name of the link/button that opens this page: a principal's school, a teacher's students (as in Poems). */
export function schoolLinkLabel(role) {
  return role === "principal" ? "My School" : "My Students";
}

// ------------------------------------------------------------------ styles + per-app themes
// One layout, dressed like the app it is shown in: the host passes theme="qfc" | "hifz" | "words" | "tajweed" | "stories"
// (anything else gets the neutral default). A theme is only a set of CSS variables - colours, fonts, corner radii, how the
// banner and stat cards look - so every app's School page keeps the same features while matching that app's own design.
// Fonts are not loaded here: they are the host's, inherited because the hub shares its page.
const T_PLAIN = { "--t0": "#fff", "--t1": "#fff", "--t2": "#fff", "--t3": "#fff", "--t4": "#fff" };
const THEMES = {
  // neutral fallback
  default: { "--pg": "#F5FDF8", "--surface": "#fff", "--head": "#2C5F3B", "--ink": "#2C5F3B", "--mut": "#5F6B60", "--line": "#D5F2E0", "--prim": "#4ECB7E", "--primh": "#3FB86D", "--prim-ink": "#fff",
    "--cta": "#2E9E4F", "--ctah": "#247E3F", "--acc": "#4ECB7E", "--track": "#D5F2E0", "--chip-ink": "#2C5F3B", "--soft": "#F5FDF8", "--t0": "#D5F2E0", "--t1": "#E6FCF5", "--t2": "#EFECFD", "--t3": "#FCE6F0", "--t4": "#FDF8E6",
    "--statline": "rgba(255,255,255,.7)", "--icbg": "rgba(255,255,255,.75)", "--lab": "#5F6B60", "--r": "24px", "--rs": "14px", "--rb": "999px", "--ff": "inherit", "--fh": "inherit", "--fhw": "800",
    "--shadow": "none", "--bn-bg": "linear-gradient(135deg,#D5F2E0,#F5FDF8 55%,#E6FCF5)", "--bn-ink": "#2C5F3B", "--bn-eyebrow": "#4A775A", "--bn-pill": "rgba(255,255,255,.85)", "--bn-pill-ink": "#2C5F3B" },
  // Quran Fluency: warm paper, deep emerald, amber accent; white bordered cards, big soft corners, pill buttons
  qfc: { ...T_PLAIN, "--pg": "#FDFBF7", "--surface": "#fff", "--head": "#1B2433", "--ink": "#1F2937", "--mut": "#6B7280", "--line": "#E7E0D2", "--prim": "#064E3B", "--primh": "#047857", "--prim-ink": "#fff",
    "--cta": "#064E3B", "--ctah": "#047857", "--acc": "#059669", "--track": "#E6F2EC", "--chip-ink": "#064E3B", "--soft": "#FBF9F4", "--statline": "#E7E0D2", "--icbg": "#ECFDF5", "--lab": "#6B7280",
    "--r": "26px", "--rs": "14px", "--rb": "999px", "--ff": "'Figtree', system-ui, sans-serif", "--fh": "'Outfit', 'Figtree', sans-serif", "--fhw": "600", "--shadow": "none",
    "--bn-bg": "#064E3B", "--bn-ink": "#fff", "--bn-eyebrow": "#FBBF24", "--bn-pill": "rgba(255,255,255,.14)", "--bn-pill-ink": "#fff" },
  // Hifz with Hikmah: sand page, forest green, gold small-caps labels; rounded-xl cards and buttons
  hifz: { ...T_PLAIN, "--pg": "#F9F6F0", "--surface": "#fff", "--head": "#1E3A30", "--ink": "#1A1C18", "--mut": "#6B6F66", "--line": "#E5E1D8", "--prim": "#2C5E4E", "--primh": "#234B3E", "--prim-ink": "#fff",
    "--cta": "#2C5E4E", "--ctah": "#234B3E", "--acc": "#3A7D68", "--track": "#ECE7DA", "--chip-ink": "#2C5E4E", "--soft": "#F4F0E8", "--statline": "#E5E1D8", "--icbg": "#F4EFE0", "--lab": "#B8962E",
    "--r": "20px", "--rs": "12px", "--rb": "12px", "--ff": "'Work Sans', system-ui, sans-serif", "--fh": "'Outfit', sans-serif", "--fhw": "600", "--shadow": "0 1px 2px rgba(26,28,24,.04)",
    "--bn-bg": "#1E4036", "--bn-ink": "#fff", "--bn-eyebrow": "#D4AF37", "--bn-pill": "rgba(255,255,255,.12)", "--bn-pill-ink": "#F4E7B0" },
  // Learn Words: flat editorial look - cream paper, dark green, orange monospace labels, nearly square corners
  words: { ...T_PLAIN, "--pg": "#FAF8F2", "--surface": "#fff", "--head": "#123D2A", "--ink": "#123D2A", "--mut": "#5B6B62", "--line": "#E2DCCB", "--prim": "#064E3B", "--primh": "#053D2E", "--prim-ink": "#fff",
    "--cta": "#064E3B", "--ctah": "#053D2E", "--acc": "#0F6B4F", "--track": "#E8E3D3", "--chip-ink": "#064E3B", "--soft": "#FAF8F2", "--statline": "#E2DCCB", "--icbg": "#F3EEDD", "--lab": "#C2610C",
    "--r": "6px", "--rs": "4px", "--rb": "4px", "--ff": "system-ui, -apple-system, 'Segoe UI', sans-serif", "--fh": "system-ui, -apple-system, 'Segoe UI', sans-serif", "--fhw": "800", "--shadow": "none", "--labff": "ui-monospace, SFMono-Regular, Menlo, monospace",
    "--bn-bg": "#123D2A", "--bn-ink": "#fff", "--bn-eyebrow": "#FFC400", "--bn-pill": "rgba(255,255,255,.12)", "--bn-pill-ink": "#fff" },
  // Learn Tajweed: clean slate + emerald, Space Grotesk headings, soft rounded cards (built for a phone-width frame)
  tajweed: { ...T_PLAIN, "--pg": "#F8FAFC", "--surface": "#fff", "--head": "#1E293B", "--ink": "#334155", "--mut": "#64748B", "--line": "#E2E8F0", "--prim": "#10B981", "--primh": "#059669", "--prim-ink": "#fff",
    "--cta": "#10B981", "--ctah": "#059669", "--acc": "#10B981", "--track": "#EEF2F6", "--chip-ink": "#047857", "--soft": "#F8FAFC", "--statline": "#E2E8F0", "--icbg": "#ECFDF5", "--lab": "#94A3B8",
    "--r": "24px", "--rs": "16px", "--rb": "16px", "--ff": "'Inter', system-ui, sans-serif", "--fh": "'Space Grotesk', 'Inter', sans-serif", "--fhw": "800", "--shadow": "0 1px 2px rgba(15,23,42,.06)",
    "--bn-bg": "linear-gradient(135deg,#10B981,#0D9488)", "--bn-ink": "#fff", "--bn-eyebrow": "rgba(255,255,255,.85)", "--bn-pill": "rgba(255,255,255,.2)", "--bn-pill-ink": "#fff" },
  // 600 Stories: cream, navy, coral buttons, teal tints, rounded Fredoka headings, soft shadows instead of borders
  stories: { ...T_PLAIN, "--pg": "#FAF6EC", "--surface": "#fff", "--head": "#15274D", "--ink": "#15274D", "--mut": "#5B6882", "--line": "#EFE6D2", "--prim": "#15274D", "--primh": "#0F1C3A", "--prim-ink": "#fff",
    "--cta": "#D2806D", "--ctah": "#BF6B58", "--acc": "#5E9A93", "--track": "#E3F1EC", "--chip-ink": "#3F7C74", "--soft": "#FBF8F0", "--statline": "#F1E9D6", "--icbg": "#E3F1EC", "--lab": "#5E9A93",
    "--r": "24px", "--rs": "16px", "--rb": "999px", "--ff": "'Nunito', system-ui, sans-serif", "--fh": "'Fredoka', 'Baloo 2', sans-serif", "--fhw": "700", "--shadow": "0 6px 20px rgba(21,39,77,.07)",
    "--bn-bg": "#fff", "--bn-ink": "#15274D", "--bn-eyebrow": "#5E9A93", "--bn-pill": "#E3F1EC", "--bn-pill-ink": "#3F7C74" },
};

// Layout buckets come from the hub's own width (not the screen's): it also lives inside Tajweed's phone-sized frame.
const CSS = `
.hvs{color:var(--ink);max-width:1240px;margin:0 auto;padding:0 16px 72px;font-family:var(--ff);background:var(--pg);min-height:100%;--danger:#B91C1C}
.hvs *{box-sizing:border-box}
.hvs button{font-family:inherit}
.hvs h1,.hvs h2,.hvs h3,.hvs .stat .v,.hvs .ring span,.hvs .mini b{font-family:var(--fh);font-weight:var(--fhw);color:var(--head)}
.hvs .top{position:sticky;top:0;z-index:20;background:var(--pg);border-bottom:1px solid var(--line);margin:0 -16px;padding:10px 16px;display:flex;align-items:center;justify-content:space-between;gap:12px}
.hvs .nav{display:flex;gap:4px;background:var(--surface);border:1px solid var(--line);border-radius:var(--rb);padding:4px;overflow-x:auto;max-width:100%;scrollbar-width:none}
.hvs .nav::-webkit-scrollbar{display:none}
.hvs .nav button{border:0;background:none;color:var(--mut);font-weight:700;font-size:14px;padding:8px 14px;border-radius:calc(var(--rb) - 2px);cursor:pointer;display:inline-flex;align-items:center;gap:6px;white-space:nowrap;flex:none}
.hvs .nav button:hover{background:var(--soft)}
.hvs .nav button[aria-current=page]{background:var(--prim);color:var(--prim-ink)}
.hvs .count{min-width:18px;height:18px;padding:0 5px;border-radius:999px;background:var(--cta);color:#fff;font-size:11px;font-weight:800;display:inline-flex;align-items:center;justify-content:center}
.hvs .nav button[aria-current=page] .count{background:#fff;color:var(--cta)}
.hvs .head{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap;margin:24px 0 20px}
.hvs h1{font-size:28px;margin:0}
.hvs h2{font-size:18px;margin:0 0 12px}
.hvs h3{font-size:16px;margin:0}
.hvs .sub{color:var(--mut);font-size:14px;margin-top:4px}
.hvs .banner{position:relative;overflow:hidden;border-radius:var(--r);background:var(--bn-bg);color:var(--bn-ink);border:1px solid var(--line);box-shadow:var(--shadow);padding:24px 26px;margin-bottom:20px;display:flex;align-items:center;gap:16px;flex-wrap:wrap}
.hvs .banner .ic{background:var(--bn-pill);border-radius:calc(var(--r) * .7);padding:14px;font-size:26px;line-height:1}
.hvs .banner .eyebrow{font-size:11px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--bn-eyebrow);font-family:var(--labff,inherit)}
.hvs .banner .nm{font-size:26px;font-weight:var(--fhw);font-family:var(--fh);color:var(--bn-ink)}
.hvs .pill{margin-left:auto;background:var(--bn-pill);border-radius:var(--rb);padding:8px 16px;font-size:12px;font-weight:800;color:var(--bn-pill-ink)}
.hvs .stats{display:grid;grid-template-columns:repeat(auto-fit,minmax(140px,1fr));gap:12px;margin-bottom:20px}
.hvs .stat{border-radius:calc(var(--r) * .75);border:1px solid var(--statline);padding:16px 10px;text-align:center;box-shadow:var(--shadow)}
.hvs .stat .ic{display:inline-flex;background:var(--icbg);border-radius:calc(var(--r) * .5);padding:7px;font-size:16px;margin-bottom:6px}
.hvs .stat .v{font-size:26px;line-height:1}
.hvs .stat .k{font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:var(--lab);font-weight:700;margin-top:6px;font-family:var(--labff,inherit)}
.hvs .stat .cap{font-size:10px;color:var(--mut);opacity:.75;margin-top:2px}
.hvs .t0{background:var(--t0)}.hvs .t1{background:var(--t1)}.hvs .t2{background:var(--t2)}.hvs .t3{background:var(--t3)}.hvs .t4{background:var(--t4)}
.hvs .panel{background:var(--surface);border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--shadow);padding:20px 22px;margin-bottom:20px}
.hvs .flow{display:flex;align-items:flex-start;justify-content:space-between;gap:6px;flex-wrap:wrap}
.hvs .flow .st{display:flex;flex-direction:column;align-items:center;text-align:center;gap:6px;flex:1;min-width:90px}
.hvs .flow .st .ic{border-radius:calc(var(--r) * .6);padding:13px;font-size:20px;line-height:1;background:var(--icbg)}
.hvs .flow .st .v{font-weight:var(--fhw);font-family:var(--fh);font-size:18px;color:var(--head);line-height:1}
.hvs .flow .st .l{font-size:12px;color:var(--mut);font-weight:700;max-width:120px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hvs .flow .ar{color:var(--acc);font-weight:800;margin-top:22px}
.hvs .cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(270px,1fr));gap:14px}
.hvs .tc{text-align:left;background:var(--surface);border:2px solid var(--line);border-radius:calc(var(--r) * .8);box-shadow:var(--shadow);padding:16px;cursor:pointer;display:block;width:100%;color:inherit}
.hvs .tc:hover{border-color:var(--acc)}
.hvs .tc .who{display:flex;align-items:center;gap:12px}
.hvs .av{width:42px;height:42px;border-radius:50%;background:var(--track);color:var(--chip-ink);font-weight:800;display:inline-flex;align-items:center;justify-content:center;flex:none;font-family:var(--fh)}
.hvs .meta{font-size:12px;color:var(--mut);margin-top:2px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hvs .mini{display:grid;grid-template-columns:repeat(3,1fr);gap:8px;margin-top:12px}
.hvs .mini div{background:var(--soft);border-radius:var(--rs);padding:8px;text-align:center}
.hvs .mini b{display:block;font-size:17px}.hvs .mini span{font-size:10px;text-transform:uppercase;letter-spacing:.05em;color:var(--lab);font-weight:700;font-family:var(--labff,inherit)}
.hvs .chips{display:flex;gap:6px;flex-wrap:wrap;margin-top:10px}
.hvs .chip{background:var(--track);color:var(--chip-ink);border-radius:var(--rb);padding:3px 10px;font-size:12px;font-weight:700;display:inline-flex;align-items:center;gap:4px}
.hvs .chip button{border:0;background:none;color:var(--danger);cursor:pointer;font-weight:800;padding:0 0 0 2px}
.hvs .bar{height:8px;background:var(--track);border-radius:99px;overflow:hidden;min-width:70px;flex:1}
.hvs .bar>i{display:block;height:100%;background:var(--acc);border-radius:99px}
.hvs .row{display:flex;gap:10px;align-items:center}
.hvs .btn{padding:10px 18px;border-radius:var(--rb);border:0;background:var(--cta);color:#fff;font-size:14px;font-weight:800;cursor:pointer;min-height:42px}
.hvs .btn:hover{background:var(--ctah)}
.hvs .btn.ghost{background:var(--surface);color:var(--head);border:2px solid var(--line)}
.hvs .btn.ghost:hover{border-color:var(--acc)}
.hvs .btn.danger{background:var(--surface);color:var(--danger);border:2px solid #F5C2C2}
.hvs .btn.sm{padding:6px 12px;min-height:34px;font-size:13px}
.hvs .btn:disabled{opacity:.5;cursor:default}
.hvs .rep h2{display:flex;align-items:center;gap:10px;margin:0}
.hvs .rep .scopes{display:flex;gap:8px;flex-wrap:wrap;margin:16px 0 14px}
.hvs .rep .scopes button{border:2px solid var(--line);background:var(--surface);color:var(--head);border-radius:var(--rb);padding:9px 20px;font-size:14px;font-weight:800;cursor:pointer;font-family:inherit}
.hvs .rep .scopes button[aria-pressed=true]{background:var(--head);border-color:var(--head);color:#fff}
.hvs .rep .dl{display:grid;grid-template-columns:1fr 1fr;gap:14px}
.hvs .rep .dl .btn{width:100%;min-height:54px;font-size:15px;display:inline-flex;align-items:center;justify-content:center;gap:10px}
.hvs .rep .fine{color:var(--mut);font-size:12px;text-align:center;margin-top:12px}
.hvs[data-w=sm] .rep .dl,.hvs[data-w=xs] .rep .dl{grid-template-columns:1fr}
.hvs input,.hvs textarea,.hvs select{width:100%;font:inherit;font-size:14px;padding:10px 12px;border:2px solid var(--line);border-radius:var(--rs);background:var(--soft);color:var(--ink);outline:none}
.hvs input:focus,.hvs textarea:focus,.hvs select:focus{border-color:var(--acc)}
.hvs textarea{min-height:90px;resize:vertical}
.hvs label{display:block;font-size:11px;font-weight:800;margin:12px 0 4px;text-transform:uppercase;letter-spacing:.05em;color:var(--lab);font-family:var(--labff,inherit)}
.hvs .filters{display:flex;gap:12px;flex-wrap:wrap;align-items:flex-end;margin-bottom:16px}
.hvs .filters>div{min-width:150px;flex:1;max-width:240px}.hvs .filters label{margin-top:0}
.hvs .split{display:grid;grid-template-columns:minmax(260px,380px) 1fr;gap:18px;align-items:start}
.hvs .list{display:flex;flex-direction:column;gap:8px;max-height:640px;overflow:auto;padding-right:2px}
.hvs .sc{display:flex;align-items:center;gap:10px;text-align:left;width:100%;background:var(--surface);border:2px solid var(--line);border-radius:calc(var(--r) * .65);padding:9px 12px;cursor:pointer;color:inherit}
.hvs .sc:hover{background:var(--soft)}.hvs .sc[aria-selected=true]{background:var(--track);border-color:var(--acc)}
.hvs .sc .av{width:36px;height:36px;font-size:14px}
.hvs .sc .nm{font-weight:800;font-size:14px;color:var(--head);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hvs .sc .pc{margin-left:auto;font-weight:800;font-size:13px;color:var(--head)}
.hvs .detail{background:var(--surface);border:1px solid var(--line);border-radius:var(--r);box-shadow:var(--shadow);padding:20px;position:sticky;top:76px}
.hvs .empty{color:var(--mut);padding:34px 20px;text-align:center;border:2px dashed var(--line);border-radius:calc(var(--r) * .8);background:var(--surface)}
.hvs .empty b{display:block;color:var(--head);font-size:16px;margin-bottom:4px;font-family:var(--fh)}
.hvs .ring{position:relative;width:96px;height:96px;flex:none}.hvs .ring svg{transform:rotate(-90deg)}
.hvs .ring span{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;font-size:20px}
.hvs .appr{display:flex;align-items:center;gap:10px;padding:9px 0;border-bottom:1px solid var(--line);font-size:14px}
.hvs .appr .n{width:44%;font-weight:700;color:var(--head)}.hvs .appr .s{font-size:12px;color:var(--mut)}
.hvs table{width:100%;border-collapse:collapse;font-size:14px}
.hvs th{text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:.05em;color:var(--lab);padding:8px 10px;border-bottom:2px solid var(--line);font-family:var(--labff,inherit)}
.hvs td{padding:10px;border-bottom:1px solid var(--line);vertical-align:middle}
.hvs .scroll{overflow-x:auto}
.hvs .hbar{display:flex;align-items:center;gap:10px;margin:7px 0;font-size:13px}.hvs .hbar .l{width:200px;max-width:42%;flex:none;font-weight:700;color:var(--head);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.hvs .hbar .bar{height:14px}.hvs .hbar .n{width:54px;text-align:right;font-weight:800;color:var(--head);flex:none}
.hvs .two{display:grid;grid-template-columns:1fr 1fr;gap:20px}
.hvs .err{color:var(--danger);font-size:14px;margin:8px 0;font-weight:600}
.hvs .ok{color:var(--cta);font-weight:700;font-size:14px}
.hvs .notice{border:1px solid var(--line);border-radius:calc(var(--r) * .8);box-shadow:var(--shadow);padding:14px 18px;margin-bottom:12px;background:var(--surface)}
.hvs .notice.unread{border-left:5px solid var(--acc)}.hvs .notice.pinned{background:var(--soft)}
.hvs .body{white-space:pre-wrap;margin-top:8px;font-size:14px;line-height:1.55;color:var(--ink)}
.hvs .back{border:0;background:none;color:var(--mut);font-weight:700;cursor:pointer;padding:0;margin-bottom:12px;white-space:nowrap}.hvs .back:hover{color:var(--head)}
.hvs .mask{position:fixed;inset:0;background:rgba(20,30,28,.45);z-index:100;display:flex;align-items:center;justify-content:center;padding:16px}
.hvs .modal{background:var(--surface);border-radius:var(--r);border:1px solid var(--line);padding:24px;width:100%;max-width:460px;max-height:92vh;overflow:auto;color:var(--ink)}
.hvs .modal.wide{max-width:600px}
.hvs .cred{background:var(--soft);border:1px dashed var(--acc);border-radius:var(--rs);padding:12px 14px;font-family:ui-monospace,Menlo,monospace;font-size:14px;margin:8px 0;word-break:break-all;color:var(--head)}
.hvs .grid2{display:grid;grid-template-columns:1fr 1fr;gap:10px}
/* Learn Words is flat and editorial: no emoji chips */
.hvs[data-theme=words] .stat .ic,.hvs[data-theme=words] .flow .st .ic,.hvs[data-theme=words] .banner .ic{display:none}
/* medium and narrow hubs (a tablet, a phone, or a phone-sized frame) */
.hvs[data-w=sm] .split,.hvs[data-w=xs] .split,.hvs[data-w=sm] .two,.hvs[data-w=xs] .two{grid-template-columns:1fr}
.hvs[data-w=sm] .detail,.hvs[data-w=xs] .detail{position:static}
.hvs[data-w=xs]{padding:0 12px 72px}
.hvs[data-w=xs] .top{margin:0 -12px;padding:8px 12px;flex-wrap:wrap;gap:6px}
.hvs[data-w=xs] .nav{width:100%}
.hvs[data-w=xs] .nav button{padding:8px 12px;font-size:13px}
.hvs[data-w=xs] .head{margin:16px 0 14px}
.hvs[data-w=xs] h1{font-size:22px}
.hvs[data-w=xs] .banner{padding:18px}
.hvs[data-w=xs] .banner .nm{font-size:20px}
.hvs[data-w=xs] .pill{margin-left:0}
.hvs[data-w=xs] .stats{grid-template-columns:repeat(2,1fr);gap:10px}
.hvs[data-w=xs] .panel{padding:16px 14px}
.hvs[data-w=xs] .filters>div{max-width:none;min-width:130px}
.hvs[data-w=xs] .hbar .l{width:96px}.hvs[data-w=xs] .hbar .n{width:46px}.hvs[data-w=xs] .bar{min-width:30px}
.hvs[data-w=xs] .grid2{grid-template-columns:1fr}
.hvs[data-w=xs] .flow .ar{display:none}
.hvs[data-w=xs] .flow .st{min-width:80px}
.hvs[data-w=xs] .modal{padding:18px}
`;

// ------------------------------------------------------------------ helpers
const pct = (n) => `${Math.round((Number(n) || 0) * 10) / 10}%`;
const when = (iso) => { if (!iso) return "-"; const d = new Date(iso); return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString(); };
const clamp = (v) => Math.max(0, Math.min(100, Number(v) || 0));
const Bar = ({ value }) => <div className="bar"><i style={{ width: `${clamp(value)}%` }} /></div>;
const initial = (name) => ((name || "?").trim()[0] || "?").toUpperCase();
const classLabel = (c) => `${c.class_name} ${c.section}`;
const sectionKey = (s) => `${s.class_name}\u0000${s.section}`;
const sortClasses = (a, b) => a.class_name.localeCompare(b.class_name, undefined, { numeric: true }) || a.section.localeCompare(b.section, undefined, { numeric: true });
const uniqSections = (rows) => {
  const seen = new Map();
  rows.forEach((r) => { if (r.class_name && r.section) seen.set(sectionKey(r), { class_name: r.class_name, section: r.section }); });
  return [...seen.values()].sort(sortClasses);
};

function Ring({ value }) {
  const r = 40, c = 2 * Math.PI * r;
  return (
    <div className="ring" data-testid="progress-ring">
      <svg width="96" height="96" viewBox="0 0 96 96"><circle cx="48" cy="48" r={r} fill="none" stroke="#D5F2E0" strokeWidth="10" />
        <circle cx="48" cy="48" r={r} fill="none" stroke="#4ECB7E" strokeWidth="10" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - clamp(value) / 100)} /></svg>
      <span>{Math.round(clamp(value))}%</span>
    </div>
  );
}

function Stat({ icon, value, label, caption, tint }) {
  return (
    <div className={`stat t${tint % 5}`} data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <span className="ic" aria-hidden>{icon}</span><div className="v">{value}</div><div className="k">{label}</div>{caption && <div className="cap">{caption}</div>}
    </div>
  );
}

function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const key = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", key);
    return () => document.removeEventListener("keydown", key);
  }, [onClose]);
  return (
    <div className="mask" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`modal${wide ? " wide" : ""}`} role="dialog" aria-modal="true" aria-label={title}>
        <h2>{title}</h2>{children}
      </div>
    </div>
  );
}

function Confirm({ title, text, action, busy, onYes, onNo }) {
  return (
    <Modal title={title} onClose={onNo}>
      <p style={{ color: "var(--mocha)", fontSize: 14, margin: "0 0 16px" }}>{text}</p>
      <div className="row" style={{ justifyContent: "flex-end" }}>
        <button className="btn ghost" onClick={onNo} disabled={busy}>Cancel</button>
        <button className="btn" style={{ background: "var(--danger)" }} onClick={onYes} disabled={busy} data-testid="confirm-yes">{action}</button>
      </div>
    </Modal>
  );
}

// Shown once, right after an account is created or a password reset: the gateway never shows it again.
function Credentials({ title, lines, note, onClose }) {
  const text = lines.map(([k, v]) => `${k}: ${v}`).join("\n");
  const [copied, setCopied] = useState(false);
  return (
    <Modal title={title} onClose={onClose}>
      <p style={{ color: "var(--mocha)", fontSize: 14, margin: "0 0 8px" }}>{note}</p>
      <div className="cred" data-testid="credentials">{lines.map(([k, v]) => <div key={k}>{k}: <b>{v}</b></div>)}</div>
      <div className="row" style={{ justifyContent: "flex-end", marginTop: 14 }}>
        <button className="btn ghost" onClick={() => { navigator.clipboard?.writeText(text).then(() => setCopied(true)).catch(() => {}); }}>{copied ? "Copied" : "Copy"}</button>
        <button className="btn" onClick={onClose}>Done</button>
      </div>
    </Modal>
  );
}

// ------------------------------------------------------------------ principal: overview
function Overview({ me, stats, teachers, students, go }) {
  const lic = me.license || {};
  const seats = lic.unlimited ? "Unlimited seats" : typeof lic.remaining === "number" ? `${lic.remaining} seat${lic.remaining === 1 ? "" : "s"} free of ${lic.seats}` : null;
  const hour = new Date().getHours();
  const greet = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const cards = [
    ["🎓", teachers.length, "Teachers"], ["👥", stats?.total_students ?? students.length, "Students"], ["🏫", stats?.class_count ?? "-", "Classes"],
    ["📚", stats?.section_count ?? "-", "Sections"], ["📈", pct(stats?.average_percent), "Average Progress"],
    ["⚡", stats?.active_this_week ?? "-", "Active This Week"],
  ];
  const steps = [["🏫", 1, me.school?.name], ["🎓", teachers.length, teachers.length === 1 ? "Teacher" : "Teachers"], ["🗂️", stats?.class_count ?? 0, stats?.class_count === 1 ? "Class" : "Classes"],
    ["📚", stats?.section_count ?? 0, stats?.section_count === 1 ? "Section" : "Sections"], ["👥", stats?.total_students ?? students.length, "Children"]];
  return (
    <div data-testid="school-overview">
      <div className="head"><div><h1 data-testid="school-title">{greet}, {(me.user_name || "").split(" ")[0] || "Principal"}</h1><p className="sub">Here's how your school is progressing today.</p></div></div>
      <div className="banner" data-testid="school-banner">
        <span className="ic">🏫</span>
        <div><div className="eyebrow">✨ Your school</div><div className="nm">{me.school?.name}</div></div>
        {seats && <span className="pill" data-testid="school-seats">{seats}</span>}
      </div>
      <div className="stats" data-testid="school-stats">{cards.map(([i, v, k], n) => <Stat key={k} icon={i} value={v} label={k} tint={n} />)}</div>
      <div className="panel" data-testid="school-structure">
        <h2>School structure</h2>
        <div className="flow">{steps.map(([i, v, l], n) => (
          <React.Fragment key={l + n}><div className="st"><span className={`ic t${(n + 1) % 5}`}>{i}</span><span className="v">{v}</span><span className="l" title={l}>{l}</span></div>
            {n < steps.length - 1 && <span className="ar" aria-hidden>→</span>}</React.Fragment>))}</div>
      </div>
      {!teachers.length && (
        <div className="empty"><b>No teachers yet</b>Add your first teacher to start building your school. <div style={{ marginTop: 12 }}><button className="btn" onClick={() => go("teachers")}>Add a teacher</button></div></div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ add / edit people
function ClassRows({ value, onChange }) {
  const set = (i, k, v) => onChange(value.map((c, n) => (n === i ? { ...c, [k]: v } : c)));
  return (
    <div>
      {value.map((c, i) => (
        <div className="grid2" key={i} style={{ marginBottom: 8, alignItems: "center" }}>
          <input placeholder="Class, e.g. 3rd Standard" value={c.class_name} maxLength={60} onChange={(e) => set(i, "class_name", e.target.value)} data-testid={`class-name-${i}`} />
          <div className="row"><input placeholder="Section, e.g. A" value={c.section} maxLength={60} onChange={(e) => set(i, "section", e.target.value)} data-testid={`class-section-${i}`} />
            {value.length > 1 && <button type="button" className="btn ghost sm" onClick={() => onChange(value.filter((_, n) => n !== i))} aria-label="Remove class">✕</button>}</div>
        </div>))}
      <button type="button" className="btn ghost sm" onClick={() => onChange([...value, { class_name: "", section: "" }])} data-testid="class-add-row">+ Add another class</button>
    </div>
  );
}

const cleanClasses = (rows) => rows.map((c) => ({ class_name: c.class_name.trim(), section: c.section.trim() })).filter((c) => c.class_name && c.section);

function AddTeacherDialog({ api, onClose, onDone }) {
  const [f, setF] = useState({ name: "", email: "" });
  const [classes, setClasses] = useState([{ class_name: "", section: "" }]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState(null);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const r = await api.addTeacher({ name: f.name.trim(), email: f.email.trim(), classes: cleanClasses(classes) }); setMade(r.teacher); onDone(); }
    catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  if (made) {
    return <Credentials title="Teacher added" onClose={onClose} note={`${made.name} was e-mailed a welcome message. Their temporary password is shown only once - they choose their own at first sign-in.`}
      lines={[["Sign in with", made.email], ["Temporary password", made.password]]} />;
  }
  return (
    <Modal title="Add a teacher" onClose={onClose} wide>
      <form onSubmit={submit} data-testid="add-teacher-form">
        <p style={{ color: "var(--mocha)", fontSize: 14, margin: 0 }}>They get a login for every app in your licence, and see only the classes you assign.</p>
        <label>Full name</label><input value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="teacher-name" />
        <label>E-mail</label><input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="teacher-email" />
        <label>Classes they teach</label><ClassRows value={classes} onChange={setClasses} />
        {err && <div className="err" role="alert" data-testid="form-error">{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy} data-testid="teacher-submit">{busy ? "Adding..." : "Add teacher"}</button>
        </div>
      </form>
    </Modal>
  );
}

function AddClassDialog({ teacher, api, onClose, onDone }) {
  const [rows, setRows] = useState([{ class_name: "", section: "" }]);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    const add = cleanClasses(rows);
    if (!add.length) { setErr("Enter a class and a section."); setBusy(false); return; }
    const have = new Set((teacher.classes || []).map(sectionKey));
    const merged = [...(teacher.classes || []), ...add.filter((c) => !have.has(sectionKey(c)))];
    try { await api.setTeacherClasses(teacher.user_id, merged); onDone(); onClose(); } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  return (
    <Modal title={`Add a class for ${teacher.name}`} onClose={onClose} wide>
      <form onSubmit={submit} data-testid="add-class-form"><ClassRows value={rows} onChange={setRows} />
        {err && <div className="err" role="alert">{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}><button type="button" className="btn ghost" onClick={onClose}>Cancel</button>
          <button className="btn" disabled={busy} data-testid="add-class-submit">Add</button></div>
      </form>
    </Modal>
  );
}

function AddStudentDialog({ api, me, options, defaultSection, onClose, onDone }) {
  const principal = me.role === "principal";
  const first = defaultSection || options[0] || { class_name: "", section: "" };
  const [f, setF] = useState({ name: "", age: "", gender: "", parent_mobile: "", class_name: first.class_name, section: first.section });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [made, setMade] = useState([]);
  const pick = options.find((o) => o.class_name === f.class_name && o.section === f.section);
  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const body = { name: f.name.trim(), class_name: f.class_name.trim(), section: f.section.trim(), age: Number(f.age) };
      if (f.gender) body.gender = f.gender;
      if (f.parent_mobile.trim()) body.parent_mobile = f.parent_mobile.trim();
      const r = await api.addStudents([body]);
      setMade((m) => [...m, ...r.students]);
      setF({ ...f, name: "", age: "", parent_mobile: "" });
      onDone();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  return (
    <Modal title="Add a student" onClose={onClose} wide>
      <form onSubmit={submit} data-testid="add-student-form">
        {made.length > 0 && (
          <div className="cred" data-testid="student-credentials">
            <div style={{ fontFamily: "inherit", fontSize: 12, marginBottom: 4 }}>Shown only once - note them down:</div>
            {made.map((m) => <div key={m.user_id}>{m.name} ({m.class_name} {m.section}) - username <b>{m.username}</b>, password <b>{m.password}</b></div>)}
          </div>)}
        <label>Student's name</label><input value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="student-name" />
        <div className="grid2">
          <div><label>Age</label><input type="number" min={2} max={100} value={f.age} onChange={(e) => setF({ ...f, age: e.target.value })} required data-testid="student-age" /></div>
          <div><label>Gender (optional)</label><select value={f.gender} onChange={(e) => setF({ ...f, gender: e.target.value })}><option value="">-</option><option value="male">Boy</option><option value="female">Girl</option></select></div>
        </div>
        <label>Class and section</label>
        {principal ? (
          <div className="grid2">
            <input list="hvs-classes" placeholder="Class" value={f.class_name} maxLength={60} onChange={(e) => setF({ ...f, class_name: e.target.value })} required data-testid="student-class" />
            <input placeholder="Section" value={f.section} maxLength={60} onChange={(e) => setF({ ...f, section: e.target.value })} required data-testid="student-section" />
            <datalist id="hvs-classes">{[...new Set(options.map((o) => o.class_name))].map((c) => <option key={c} value={c} />)}</datalist>
          </div>
        ) : (
          <select value={pick ? sectionKey(pick) : ""} onChange={(e) => { const [class_name, section] = e.target.value.split("\u0000"); setF({ ...f, class_name, section }); }} required data-testid="student-class-select">
            {options.map((o) => <option key={sectionKey(o)} value={sectionKey(o)}>{classLabel(o)}</option>)}
          </select>)}
        <label>Parent's mobile (optional)</label><input value={f.parent_mobile} maxLength={20} onChange={(e) => setF({ ...f, parent_mobile: e.target.value })} />
        {err && <div className="err" role="alert" data-testid="form-error">{err}</div>}
        <div className="row" style={{ justifyContent: "flex-end", marginTop: 16 }}>
          <button type="button" className="btn ghost" onClick={onClose}>{made.length ? "Done" : "Cancel"}</button>
          <button className="btn" disabled={busy || !f.class_name || !f.section} data-testid="student-submit">{busy ? "Adding..." : made.length ? "Add another" : "Add student"}</button>
        </div>
      </form>
    </Modal>
  );
}

// ------------------------------------------------------------------ students (both roles)
// When a host app sets `onlyApp` (Quranic Poems does), the page shows that one app's progress only, never the others.
const OnlyAppCtx = React.createContext(null);

function StudentPanel({ api, me, student, onRemoved }) {
  const only = React.useContext(OnlyAppCtx);
  const [state, setState] = useState({ loading: true });
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  useEffect(() => {
    let alive = true;
    setState({ loading: true }); setErr("");
    api.student(student.user_id).then((d) => alive && setState({ d })).catch((e) => alive && setState({ error: e.message }));
    return () => { alive = false; };
  }, [api, student.user_id]);
  const reset = async () => {
    setBusy(true); setErr("");
    try { const r = await api.resetStudent(student.user_id); setDlg({ type: "creds", r }); } catch (e) { setErr(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try { await api.removeStudent(student.user_id); setDlg(null); onRemoved(); } catch (e) { setErr(e.message); setDlg(null); } finally { setBusy(false); }
  };
  if (state.loading) return <div className="detail">Loading...</div>;
  if (state.error) return <div className="detail"><div className="err">{state.error}</div></div>;
  const { student: s, progress: p } = state.d;
  const apps = (p?.apps || []).filter((a) => !only || a.product === only.product);
  const ringValue = only ? (apps[0] && apps[0].status === "ok" ? apps[0].percent : 0) : p?.percent;
  return (
    <div className="detail" data-testid="student-detail">
      <div className="row" style={{ gap: 14, flexWrap: "wrap" }}>
        <span className="av" style={{ width: 52, height: 52, fontSize: 20 }}>{initial(s.name)}</span>
        <div style={{ minWidth: 0, flex: 1 }}>
          <h2 style={{ margin: 0 }} data-testid="student-detail-name">{s.name}</h2>
          <div className="meta">{s.class_name} {s.section}{s.age ? ` · age ${s.age}` : ""}{s.gender ? ` · ${s.gender === "male" ? "boy" : "girl"}` : ""}</div>
          <div className="meta">Username: {s.username || "-"} · last active {when(s.last_active_at)}</div>
        </div>
        <Ring value={ringValue} />
      </div>
      <h3 style={{ margin: "18px 0 4px" }}>{only ? `Progress in ${only.name}` : "Progress in each app"}</h3>
      {apps.length === 0 ? <div className="empty" style={{ padding: 18 }}>No app activity yet.</div> : apps.map((a) => (
        <div className="appr" key={a.product} data-testid={`app-${a.product}`}>
          <div className="n">{a.name}<div className="s">{a.status === "ok" ? (a.summary || "") : a.status === "not_started" ? "Not started yet" : "Unavailable right now"}</div></div>
          <Bar value={a.status === "ok" ? a.percent : 0} /><b style={{ width: 52, textAlign: "right" }}>{a.status === "ok" ? pct(a.percent) : "-"}</b>
        </div>))}
      {err && <div className="err" role="alert">{err}</div>}
      <div className="row" style={{ marginTop: 16, flexWrap: "wrap" }}>
        <button className="btn ghost sm" onClick={reset} disabled={busy} data-testid="student-reset">Reset password</button>
        {me.role === "principal" && <button className="btn danger sm" onClick={() => setDlg({ type: "remove" })} disabled={busy} data-testid="student-remove">Remove student</button>}
      </div>
      {dlg?.type === "creds" && <Credentials title="New password" onClose={() => setDlg(null)} note="Shown only once. The student signs in with their username." lines={[["Username", dlg.r.username], ["Temporary password", dlg.r.password]]} />}
      {dlg?.type === "remove" && <Confirm title={`Remove ${s.name}?`} text="They can no longer sign in and their licence is freed. This can't be undone from here." action="Remove" busy={busy} onYes={remove} onNo={() => setDlg(null)} />}
    </div>
  );
}

function Students({ api, me, students, teachers, onReload, preselect }) {
  const principal = me.role === "principal";
  const [q, setQ] = useState("");
  const [teacherId, setTeacherId] = useState("");
  const [cls, setCls] = useState("");
  const [sec, setSec] = useState("");
  const [open, setOpen] = useState(preselect || null);
  const [adding, setAdding] = useState(false);

  const mySections = useMemo(() => (principal ? uniqSections([...students, ...teachers.flatMap((t) => t.classes || [])]) : uniqSections(me.classes || [])), [principal, students, teachers, me.classes]);
  const teacherSections = useMemo(() => {
    const t = teachers.find((x) => x.user_id === teacherId);
    return t ? new Set((t.classes || []).map(sectionKey)) : null;
  }, [teachers, teacherId]);
  const classNames = useMemo(() => [...new Set(mySections.filter((s) => !teacherSections || teacherSections.has(sectionKey(s))).map((s) => s.class_name))], [mySections, teacherSections]);
  const sections = useMemo(() => mySections.filter((s) => s.class_name === cls && (!teacherSections || teacherSections.has(sectionKey(s)))), [mySections, cls, teacherSections]);

  useEffect(() => { if (cls && !classNames.includes(cls)) { setCls(""); setSec(""); } }, [classNames, cls]);
  useEffect(() => { if (sec && !sections.some((s) => s.section === sec)) setSec(""); }, [sections, sec]);

  const shown = useMemo(() => students.filter((s) => {
    if (teacherSections && !teacherSections.has(sectionKey(s))) return false;
    if (cls && s.class_name !== cls) return false;
    if (sec && s.section !== sec) return false;
    return `${s.name} ${s.username || ""}`.toLowerCase().includes(q.trim().toLowerCase());
  }), [students, teacherSections, cls, sec, q]);

  const summary = useMemo(() => {
    const started = shown.filter((s) => s.percent > 0);
    return { total: shown.length, active: started.length, avg: shown.length ? shown.reduce((a, s) => a + s.percent, 0) / shown.length : 0, not: shown.length - started.length };
  }, [shown]);
  const selected = shown.find((s) => s.user_id === open) || null;
  const defaultSection = cls && sec ? { class_name: cls, section: sec } : null;
  const canAdd = principal || mySections.length > 0;

  return (
    <div data-testid="school-students">
      <div className="head">
        <div><h1>{principal ? "All Students" : "My Students"}</h1><p className="sub">{principal ? "Every child in your school. Pick one to see their progress." : "The children in your classes. Pick one to see their progress in detail."}</p></div>
        {canAdd && <button className="btn" onClick={() => setAdding(true)} data-testid="add-student-open">+ Add student</button>}
      </div>
      <div className="filters">
        <div><label>Search</label><input placeholder="Name or username" value={q} onChange={(e) => setQ(e.target.value)} data-testid="student-search" /></div>
        {principal && teachers.length > 0 && <div><label>Teacher</label><select value={teacherId} onChange={(e) => setTeacherId(e.target.value)} data-testid="filter-teacher"><option value="">All teachers</option>{teachers.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}</select></div>}
        <div><label>Class</label><select value={cls} onChange={(e) => { setCls(e.target.value); setSec(""); }} data-testid="filter-class"><option value="">All classes</option>{classNames.map((c) => <option key={c} value={c}>{c}</option>)}</select></div>
        {cls && <div><label>Section</label><select value={sec} onChange={(e) => setSec(e.target.value)} data-testid="filter-section"><option value="">All sections</option>{sections.map((s) => <option key={s.section} value={s.section}>{s.section}</option>)}</select></div>}
      </div>
      <div className="stats" data-testid="students-summary">
        <Stat icon="👥" value={summary.total} label="Students" tint={0} /><Stat icon="⚡" value={summary.active} label="Active" tint={1} />
        <Stat icon="📈" value={pct(summary.avg)} label="Avg Progress" tint={2} /><Stat icon="🌱" value={summary.not} label="Not Started" tint={3} />
      </div>
      {students.length === 0 ? (
        <div className="empty" data-testid="no-students"><b>No students yet</b>{principal ? "Add your first student, or ask your teachers to add the children in their classes." : "Students added to your classes will show up here."}</div>
      ) : (
        <div className="split">
          <div className="list" data-testid="student-list">
            {shown.length === 0 && <div className="empty">No students match.</div>}
            {shown.map((s) => (
              <button key={s.user_id} className="sc" aria-selected={open === s.user_id} onClick={() => setOpen(s.user_id)} data-testid={`student-${s.user_id}`}>
                <span className="av">{initial(s.name)}</span>
                <span style={{ minWidth: 0 }}><span className="nm" style={{ display: "block" }}>{s.name}</span><span className="meta" style={{ display: "block" }}>{s.class_name} {s.section}</span></span>
                <span className="pc">{s.reported ? pct(s.percent) : "-"}</span>
              </button>))}
          </div>
          {selected ? <StudentPanel key={selected.user_id} api={api} me={me} student={selected} onRemoved={() => { setOpen(null); onReload(); }} />
            : <div className="empty" data-testid="no-student-selected"><b>Select a student</b>Choose a student from the list to see their progress in every app.</div>}
        </div>)}
      {adding && <AddStudentDialog api={api} me={me} options={mySections} defaultSection={defaultSection} onClose={() => setAdding(false)} onDone={onReload} />}
    </div>
  );
}

// ------------------------------------------------------------------ principal: teachers
function TeacherDetail({ api, teacher, students, onBack, onReload, openStudent }) {
  const [dlg, setDlg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const keys = new Set((teacher.classes || []).map(sectionKey));
  const mine = students.filter((s) => keys.has(sectionKey(s)));
  const drop = async (c) => {
    setErr("");
    try { await api.setTeacherClasses(teacher.user_id, (teacher.classes || []).filter((x) => sectionKey(x) !== sectionKey(c))); await onReload(); } catch (e) { setErr(e.message); }
  };
  const reset = async () => { setBusy(true); setErr(""); try { setDlg({ type: "creds", r: await api.resetTeacher(teacher.user_id) }); } catch (e) { setErr(e.message); } finally { setBusy(false); } };
  const remove = async () => { setBusy(true); try { await api.removeTeacher(teacher.user_id); await onReload(); onBack(); } catch (e) { setErr(e.message); setDlg(null); } finally { setBusy(false); } };
  return (
    <div data-testid="teacher-detail">
      <button className="back" onClick={onBack} data-testid="teacher-back">← All teachers</button>
      <div className="head">
        <div className="row"><span className="av" style={{ width: 52, height: 52, fontSize: 20 }}>{initial(teacher.name)}</span>
          <div><h1 style={{ fontSize: 24 }}>{teacher.name}</h1><p className="sub">{teacher.email} · last active {when(teacher.last_active_at)}</p></div></div>
        <div className="row" style={{ flexWrap: "wrap" }}>
          <button className="btn ghost sm" onClick={reset} disabled={busy} data-testid="teacher-reset">Reset password</button>
          <button className="btn danger sm" onClick={() => setDlg({ type: "remove" })} disabled={busy} data-testid="teacher-remove">Remove teacher</button>
        </div>
      </div>
      <div className="stats">
        <Stat icon="👥" value={teacher.student_count} label="Students" tint={0} /><Stat icon="⚡" value={teacher.active_students} label="Active" tint={1} />
        <Stat icon="📈" value={pct(teacher.average_percent)} label="Avg Progress" tint={2} /><Stat icon="🗂️" value={(teacher.classes || []).length} label="Classes" tint={3} />
      </div>
      <div className="panel">
        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap" }}><h2 style={{ margin: 0 }}>Classes</h2><button className="btn ghost sm" onClick={() => setDlg({ type: "class" })} data-testid="add-class-open">+ Add a class</button></div>
        <div className="chips" data-testid="teacher-classes">{(teacher.classes || []).length === 0 ? <span className="sub">No classes assigned yet.</span> : teacher.classes.map((c) => (
          <span className="chip" key={sectionKey(c)}>{classLabel(c)}<button onClick={() => drop(c)} aria-label={`Remove ${classLabel(c)}`}>✕</button></span>))}</div>
      </div>
      {err && <div className="err" role="alert">{err}</div>}
      <div className="panel">
        <h2>Students ({mine.length})</h2>
        {mine.length === 0 ? <div className="sub">No students in this teacher's classes yet.</div> : (
          <div className="scroll"><table data-testid="teacher-students"><thead><tr><th>Student</th><th>Class</th><th style={{ width: "34%" }}>Progress</th><th>Last active</th></tr></thead><tbody>
            {mine.map((s) => (
              <tr key={s.user_id} style={{ cursor: "pointer" }} onClick={() => openStudent(s.user_id)}><td><b>{s.name}</b></td><td>{s.class_name} {s.section}</td>
                <td><div className="row"><Bar value={s.percent} /><span>{s.reported ? pct(s.percent) : "-"}</span></div></td><td>{when(s.last_active_at)}</td></tr>))}
          </tbody></table></div>)}
      </div>
      {dlg?.type === "class" && <AddClassDialog teacher={teacher} api={api} onClose={() => setDlg(null)} onDone={onReload} />}
      {dlg?.type === "creds" && <Credentials title="New password" onClose={() => setDlg(null)} note="Shown only once. They choose their own password at next sign-in." lines={[["Sign in with", teacher.email], ["Temporary password", dlg.r.password]]} />}
      {dlg?.type === "remove" && <Confirm title={`Remove ${teacher.name}?`} text="They can no longer sign in and their licence is freed. Their students stay in the school." action="Remove" busy={busy} onYes={remove} onNo={() => setDlg(null)} />}
    </div>
  );
}

function Teachers({ api, teachers, students, onReload, openStudent }) {
  const [sel, setSel] = useState(null);
  const [adding, setAdding] = useState(false);
  const teacher = teachers.find((t) => t.user_id === sel);
  if (teacher) return <TeacherDetail api={api} teacher={teacher} students={students} onBack={() => setSel(null)} onReload={onReload} openStudent={openStudent} />;
  return (
    <div data-testid="school-teachers">
      <div className="head"><div><h1>Teachers</h1><p className="sub">Everyone teaching in your school. Open a teacher to see their classes and students.</p></div>
        <button className="btn" onClick={() => setAdding(true)} data-testid="add-teacher-open">+ Add teacher</button></div>
      {teachers.length === 0 ? <div className="empty" data-testid="no-teachers"><b>No teachers yet</b>Add a teacher to give them their own classes and students.</div> : (
        <div className="cards" data-testid="teacher-list">{teachers.map((t) => (
          <button key={t.user_id} className="tc" onClick={() => setSel(t.user_id)} data-testid={`teacher-${t.user_id}`}>
            <div className="who"><span className="av">{initial(t.name)}</span><div style={{ minWidth: 0 }}><h3>{t.name}</h3><div className="meta">{t.email}</div></div></div>
            <div className="chips">{(t.classes || []).length ? t.classes.slice(0, 4).map((c) => <span className="chip" key={sectionKey(c)}>{classLabel(c)}</span>) : <span className="meta">No classes yet</span>}{(t.classes || []).length > 4 && <span className="chip">+{t.classes.length - 4}</span>}</div>
            <div className="mini"><div><b>{t.student_count}</b><span>Students</span></div><div><b>{t.active_students}</b><span>Active</span></div><div><b>{pct(t.average_percent)}</b><span>Avg</span></div></div>
          </button>))}</div>)}
      {adding && <AddTeacherDialog api={api} onClose={() => setAdding(false)} onDone={onReload} />}
    </div>
  );
}

// ------------------------------------------------------------------ progress report download
const REPORT_SCOPES = [["overall", "Overall"], ["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]];

// "Download progress report": the whole school (principal) or the teacher's own classes, as a PDF or an Excel file.
function ReportPanel({ api, principal }) {
  const only = React.useContext(OnlyAppCtx);
  const [scope, setScope] = useState("overall");
  const [busy, setBusy] = useState(null);
  const [err, setErr] = useState("");
  const get = async (format) => {
    setBusy(format); setErr("");
    try {
      const { blob, name } = await api.report(scope, format, only ? only.product : undefined);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = name; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e.message || "Could not build the report"); } finally { setBusy(null); }
  };
  return (
    <div className="rep" data-testid="school-report">
      <h2><span aria-hidden="true">⬇️</span> Download progress report</h2>
      <p className="sub">Pick a report below, then download it as a PDF or an Excel file — {principal ? "every student and every teacher" : "every student in your classes"} in one document.</p>
      <div className="panel" style={{ marginTop: 14 }}>
        <div className="scopes" data-testid="report-scope">{REPORT_SCOPES.map(([k, l]) => (
          <button key={k} type="button" aria-pressed={scope === k} onClick={() => setScope(k)} data-testid={`report-scope-${k}`}>{l}</button>))}</div>
        <div className="dl">
          <button type="button" className="btn" disabled={!!busy} onClick={() => get("pdf")} data-testid="report-download-pdf">📄 {busy === "pdf" ? "Building PDF…" : "Download PDF"}</button>
          <button type="button" className="btn ghost" disabled={!!busy} onClick={() => get("xlsx")} data-testid="report-download-xlsx">📊 {busy === "xlsx" ? "Building Excel…" : "Download Excel"}</button>
        </div>
        {err && <div className="empty" role="alert" style={{ marginTop: 14 }}>{err}</div>}
        <p className="fine">Status, overall progress per app, last activity and whether each person was active in the period, as things stand right now.</p>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ principal: school progress
function HBars({ rows, testid }) {
  return (
    <div data-testid={testid}>{rows.map((r) => (
      <div className="hbar" key={r.label}><span className="l" title={r.label}>{r.label}</span><Bar value={r.value} /><span className="n">{r.text ?? pct(r.value)}</span></div>))}</div>
  );
}

function Progress({ stats, teachers, openStudent }) {
  const only = React.useContext(OnlyAppCtx);
  if (!stats) return <div className="empty">Progress isn't available right now.</div>;
  const dist = stats.distribution || {};
  const total = Math.max(1, stats.total_students);
  const labels = { not_started: "Not started", "1-25": "1-25%", "26-50": "26-50%", "51-75": "51-75%", "76-100": "76-100%" };
  return (
    <div data-testid="school-progress">
      <div className="head"><div><h1>School Progress</h1><p className="sub">{only ? `How your students are doing in ${only.name}.` : "How your students are doing across every app."}</p></div></div>
      <div className="stats">
        <Stat icon="👥" value={stats.total_students} label="Students" tint={0} /><Stat icon="⚡" value={stats.active_students} label="Started Learning" tint={1} />
        <Stat icon="📅" value={stats.active_this_week} label="Active This Week" tint={2} /><Stat icon="📈" value={pct(stats.average_percent)} label="Average Progress" tint={3} />
      </div>
      <div className="two" style={only ? { gridTemplateColumns: "1fr" } : undefined}>
        <div className="panel"><h2>How far students have got</h2>
          <HBars testid="chart-distribution" rows={Object.keys(labels).map((k) => ({ label: labels[k], value: (100 * (dist[k] || 0)) / total, text: String(dist[k] || 0) }))} /></div>
        {!only && (
        <div className="panel"><h2>Average progress by app</h2>
          {stats.by_app?.length ? <HBars testid="chart-apps" rows={stats.by_app.map((a) => ({ label: a.name, value: a.average_percent }))} /> : <div className="sub">No app activity yet.</div>}</div>
        )}
      </div>
      <div className="panel"><h2>Average progress by class</h2>
        {stats.by_class?.length ? <HBars testid="chart-classes" rows={stats.by_class.map((c) => ({ label: `${c.class} (${c.students})`, value: c.average_percent }))} /> : <div className="sub">No students yet.</div>}</div>
      <div className="two">
        <div className="panel"><h2>Teachers</h2>
          {teachers.length === 0 ? <div className="sub">No teachers yet.</div> : (
            <div className="scroll"><table data-testid="progress-teachers"><thead><tr><th>Teacher</th><th>Students</th><th style={{ width: "40%" }}>Average</th></tr></thead><tbody>
              {teachers.map((t) => <tr key={t.user_id}><td><b>{t.name}</b></td><td>{t.student_count}</td><td><div className="row"><Bar value={t.average_percent} /><span>{pct(t.average_percent)}</span></div></td></tr>)}
            </tbody></table></div>)}</div>
        <div className="panel"><h2>Needs attention</h2>
          {stats.needs_attention?.length ? (
            <div className="scroll"><table data-testid="needs-attention"><tbody>
              {stats.needs_attention.map((s) => <tr key={s.user_id} style={{ cursor: "pointer" }} onClick={() => openStudent(s.user_id)}><td><b>{s.name}</b><div className="meta">{s.class_name} {s.section}</div></td><td style={{ textAlign: "right" }}>{s.percent > 0 ? pct(s.percent) : "Not started"}</td></tr>)}
            </tbody></table></div>) : <div className="sub">Nobody to flag yet.</div>}</div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ notices (both roles)
function NoticeBoard({ api, me, onUnread }) {
  const [data, setData] = useState({ notices: [], unread_count: 0 });
  const [staff, setStaff] = useState(null);
  const [form, setForm] = useState({ title: "", body: "", pinned: false, expires_at: "" });
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [del, setDel] = useState(null);
  const isPrincipal = me.role === "principal";

  const load = useCallback(async () => {
    try {
      const [n, c] = await Promise.all([api.notices(), api.staffCount().catch(() => null)]);
      setData(n); setStaff(c?.staff_count ?? null); onUnread?.(n.unread_count);
    } catch (e) { setErr(e.message); }
  }, [api, onUnread]);
  useEffect(() => { load(); }, [load]);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const expires = form.expires_at ? new Date(form.expires_at).toISOString() : (editing ? "" : null);
      if (editing) await api.updateNotice(editing, { title: form.title, body: form.body, expires_at: expires, ...(isPrincipal ? { pinned: form.pinned } : {}) });
      else await api.createNotice({ title: form.title, body: form.body, pinned: form.pinned, expires_at: expires || null });
      setForm({ title: "", body: "", pinned: false, expires_at: "" }); setEditing(null); await load();
    } catch (e2) { setErr(e2.message); } finally { setBusy(false); }
  };
  const read = async (n) => { if (!n.read) { try { await api.markRead(n.notice_id); await load(); } catch { /* ignore */ } } };
  const remove = async () => { try { await api.deleteNotice(del.notice_id); setDel(null); await load(); } catch (e) { setErr(e.message); setDel(null); } };
  const edit = (n) => { setEditing(n.notice_id); setForm({ title: n.title, body: n.body, pinned: n.pinned, expires_at: n.expires_at ? n.expires_at.slice(0, 16) : "" }); window.scrollTo?.({ top: 0, behavior: "smooth" }); };

  return (
    <div data-testid="school-notices">
      <div className="head"><div><h1>Notice Board</h1><p className="sub">Messages between the principal and teachers. Students never see this.</p></div></div>
      <form className="panel" onSubmit={submit}>
        <h2>{editing ? "Edit notice" : "Post a notice to your school's staff"}</h2>
        <label>Title</label><input value={form.title} maxLength={200} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="notice-title" />
        <label>Message</label><textarea value={form.body} maxLength={8000} onChange={(e) => setForm({ ...form, body: e.target.value })} required data-testid="notice-body" />
        <label>Hide after (optional)</label><input type="datetime-local" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} style={{ maxWidth: 260 }} />
        {isPrincipal && <label style={{ display: "flex", gap: 8, alignItems: "center", textTransform: "none", letterSpacing: 0, fontSize: 14 }}><input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} style={{ width: "auto" }} /> Pin to the top</label>}
        {err && <div className="err" role="alert">{err}</div>}
        <div className="row" style={{ marginTop: 12 }}>
          <button className="btn" disabled={busy} data-testid="notice-submit">{editing ? "Save changes" : "Post notice"}</button>
          {editing && <button type="button" className="btn ghost" onClick={() => { setEditing(null); setForm({ title: "", body: "", pinned: false, expires_at: "" }); }}>Cancel</button>}
        </div>
      </form>
      {data.notices.length === 0 ? <div className="empty"><b>No notices yet</b>Be the first to post one.</div> : data.notices.map((n) => (
        <div key={n.notice_id} className={`notice${n.read ? "" : " unread"}${n.pinned ? " pinned" : ""}`} onMouseEnter={() => read(n)} data-testid={`notice-${n.notice_id}`}>
          <h3>{n.pinned ? "📌 " : ""}{n.title}</h3>
          <div className="meta">{n.created_by} ({n.created_by_role}) · {when(n.created_at)}{n.expires_at ? ` · hides ${when(n.expires_at)}` : ""}{staff ? ` · seen by ${n.read_count} of ${staff}` : ""}</div>
          <div className="body">{n.body}</div>
          {n.can_manage && <div className="row" style={{ marginTop: 10 }}><button className="btn ghost sm" onClick={() => edit(n)}>Edit</button><button className="btn danger sm" onClick={() => setDel(n)}>Delete</button></div>}
        </div>))}
      {del && <Confirm title="Delete this notice?" text={`"${del.title}" will be removed for everyone.`} action="Delete" onYes={remove} onNo={() => setDel(null)} />}
    </div>
  );
}


// The hub's own width bucket ("xs" phone-sized, "sm" tablet-sized, "lg"), kept on the root as data-w for the CSS above.
function useWidthBucket() {
  const ref = useRef(null);
  const [w, setW] = useState("lg");
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const bucket = (px) => (px <= 560 ? "xs" : px <= 860 ? "sm" : "lg");
    setW(bucket(el.getBoundingClientRect().width));
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver((entries) => setW(bucket(entries[0].contentRect.width + 32)));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

// ------------------------------------------------------------------ page
export default function SchoolHub({ api, onBack = undefined, theme = "default", onlyApp = undefined }) {
  const vars = THEMES[theme] || THEMES.default;
  const [rootRef, width] = useWidthBucket();
  const [state, setState] = useState({ loading: true });
  const [view, setView] = useState(null);
  const [unread, setUnread] = useState(0);
  const [pick, setPick] = useState(null);

  const load = useCallback(async () => {
    try {
      const me = await api.me();
      const principal = me.role === "principal";
      const [students, stats, teachers, notices] = await Promise.all([
        api.students(), principal ? api.analytics().catch(() => null) : Promise.resolve(null),
        principal ? api.teachers().then((r) => r.teachers) : Promise.resolve([]),
        api.notices().catch(() => ({ unread_count: 0 })),
      ]);
      let st = students.students, sts = stats, ts = teachers, only = null;
      if (onlyApp) {
        // Show one app's progress only: swap every overall figure for that app's own.
        try {
          const ap = await api.appProgress(onlyApp);
          only = { product: onlyApp, name: ap.app_name || onlyApp };
          const byId = Object.fromEntries((ap.students || []).map((r) => [r.user_id, r]));
          st = st.map((x) => { const r = byId[x.user_id]; const ok = !!r && r.status === "ok"; return { ...x, percent: ok ? r.percent : 0, reported: ok }; });
          const avg = (arr) => (arr.length ? Math.round((arr.reduce((t, x) => t + x.percent, 0) / arr.length) * 10) / 10 : 0);
          const band = (p) => (p <= 0 ? "not_started" : p <= 25 ? "1-25" : p <= 50 ? "26-50" : p <= 75 ? "51-75" : "76-100");
          const groups = {};
          st.forEach((x) => { const k = `${x.class_name || ""} ${x.section || ""}`.trim() || "-"; (groups[k] = groups[k] || []).push(x); });
          if (sts) {
            sts = {
              ...sts, active_students: st.filter((x) => x.percent > 0).length, average_percent: avg(st),
              distribution: st.reduce((d, x) => { const k = band(x.percent); d[k] = (d[k] || 0) + 1; return d; }, { not_started: 0, "1-25": 0, "26-50": 0, "51-75": 0, "76-100": 0 }),
              by_app: [{ name: only.name, average_percent: avg(st) }],
              by_class: Object.keys(groups).sort().map((k) => ({ class: k, students: groups[k].length, average_percent: avg(groups[k]) })),
              needs_attention: [...st].sort((a, b) => a.percent - b.percent).slice(0, 5).filter((x) => x.percent < 50),
            };
          }
          ts = ts.map((t) => {
            const keys = new Set((t.classes || []).map((c) => `${c.class_name}|${c.section}`));
            const mine = st.filter((x) => keys.has(`${x.class_name}|${x.section}`));
            return { ...t, average_percent: avg(mine), active_students: mine.filter((x) => x.percent > 0).length };
          });
        } catch { only = null; /* fall back to the overall figures rather than showing nothing */ }
      }
      setState({ me, students: st, stats: sts, teachers: ts, only });
      setUnread(notices.unread_count || 0);
      setView((v) => v || (principal ? "overview" : "students"));
    } catch (e) { setState({ error: e.status === 403 ? "This page is for school principals and teachers." : e.message }); }
  }, [api, onlyApp]);
  useEffect(() => { load(); }, [load]);

  if (state.loading) return <div className="hvs" ref={rootRef} data-w={width} data-theme={theme} style={vars}><style>{CSS}</style><p style={{ padding: 24 }}>Loading your school...</p></div>;
  if (state.error) return <div className="hvs" ref={rootRef} data-w={width} data-theme={theme} style={vars}><style>{CSS}</style><div className="empty" role="alert" style={{ marginTop: 24 }}>{state.error}</div></div>;
  const { me, students, stats, teachers, only } = state;
  const principal = me.role === "principal";
  const tabs = principal
    ? [["overview", "Overview"], ["teachers", "Teachers"], ["students", "Students"], ["progress", "Progress"], ["notices", "Notices"]]
    : [["students", "My Students"], ["notices", "Notices"]];
  const openStudent = (id) => { setPick(id); setView("students"); };
  const go = (v) => { setPick(null); setView(v); };

  return (
    <OnlyAppCtx.Provider value={only || null}>
    <div className="hvs" ref={rootRef} data-w={width} data-theme={theme} style={vars} data-testid="school-hub">
      <style>{CSS}</style>
      <div className="top">
        {onBack ? <button className="back" style={{ margin: 0 }} onClick={onBack} data-testid="school-back">← Back</button> : <span />}
        <nav className="nav" data-testid="school-nav">{tabs.map(([id, label]) => (
          <button key={id} aria-current={view === id ? "page" : undefined} onClick={() => go(id)} data-testid={`school-nav-${id}`}>
            {label}{id === "notices" && unread > 0 && <span className="count" data-testid="school-notices-badge">{unread}</span>}</button>))}</nav>
      </div>
      {view === "overview" && principal && <Overview me={me} stats={stats} teachers={teachers} students={students} go={go} />}
      {view === "teachers" && principal && <Teachers api={api} teachers={teachers} students={students} onReload={load} openStudent={openStudent} />}
      {view === "students" && <Students key={pick || "all"} api={api} me={me} students={students} teachers={teachers} onReload={load} preselect={pick} />}
      {view === "progress" && principal && <><Progress stats={stats} teachers={teachers} openStudent={openStudent} /><ReportPanel api={api} principal /></>}
      {view === "students" && !principal && <ReportPanel api={api} principal={false} />}
      {view === "notices" && <NoticeBoard api={api} me={me} onUnread={setUnread} />}
    </div>
    </OnlyAppCtx.Provider>
  );
}
